import React, { Component } from 'react';
import _ from 'lodash';
import { set } from 'lodash/fp';
import Axios from 'axios';
import { Question, QuestionGroup } from '../Question';
import { Button } from '../styles';
import {
  Flex, Text, ActionNote, DisabledActionButton, Loading,
} from '../components';
import { isActionAssigneeMatchCompanyStaffV2 } from '../../../utils';
import { getErrorMessage } from '../../../assets/js/utils';
import { PageTitle } from '../biz-components/page-title';

const TYPES = {
  single_question: 'single question',
  group_question: 'group questions',
};

const streetLineOneKey = 'address/streetLineOne';
const cityKey = 'address/city';
const countrySubdivisionCodeKey = 'address/countrySubdivisionCode';
const postalCodeKey = 'address/postalCode';
const countryCodeKey = 'address/countryCode';
const amountKey = 'compensation/payrate/amount';
const rateTypeKey = 'compensation/payrate/rateType';
const supervisorWorkerIdKey = 'supervisor/workerId';
const companyKey = 'companyId';
const jobTitleKey = 'job/jobTitleId';
const organizationKey = 'organization/organizationId';
const supervisorKey = 'supervisor/workerId';
const emergencyContactKey = 'emergencyContact'
const emergencyContactGivenName = `${emergencyContactKey}/relationship/person/name/givenName`;
const emergencyContactFamilyName = `${emergencyContactKey}/relationship/person/name/familyName`;
const emergencyContactPhoneNumber = `${emergencyContactKey}/relationship/person/communication/telecom/dialNumber`;
const emergencyContactEmail = `${emergencyContactKey}/relationship/person/communication/email/uri`;
const emergencyContactRelationshipTypeKey = `${emergencyContactKey}/relationship/relationshipType/relationshipTypeId`;

const addressNote = 'If city, state, zip code or country is specified then this field is mandatory.';
const payrateNote = 'If pay frequency is specified then this field is mandatory.';
const selectCountryMessage = 'Please select country first.';
const selectCompanyMessage = 'Please select Paychex company first.';
const emergencyContactNote = 'Please fill out all emergency contact fields or leave them all blank.';

const subSectionTitlePersonalInformation = 'Personal Information';
const subSectionTitleEmergencyContact = 'Emergency Contact';
const subSectionTitleFederalTax = 'Federal Taxes';

const emergencyContactGroupKey = 'emergencyContact/'
const federalTaxKey = 'federaltax/'

const renderSubTitle = (title) => {
  return (
    <div>
      <Text fontSize='18px' semibold>{title}</Text>
      {subSectionTitleFederalTax === title ? (
          <a href='https://www.irs.gov/forms-pubs/about-form-w-4' target="_blank">
            <Text fontSize='12px'>Learn more about the W4 form</Text>
          </a>
      ) : <></>
      }
      <br />
    </div>
  )
}

const getComponentType = (question) => {
  const { fields } = question;
  if (fields === undefined) return TYPES.single_question;
  if (fields) return TYPES.group_question;
  return undefined;
};

const getNestedFields = (question) => {
  const { fields } = question;
  const nestFields = fields.filter((field) => field.fields);
  return nestFields;
};

const getQuestionInputName = (question, fieldKey, rowIndex, nestedFieldKey) => {
  const componentType = getComponentType(question);
  let name = '';
  const isNestedQuestionGroup = nestedFieldKey
    && rowIndex !== undefined
    && getNestedFields(question).length !== 0;
  const { onboarding_form_question_id: questionId } = question;

  switch (componentType) {
    case TYPES.single_question:
      name = `${questionId}[${question.key}]`;
      break;
    case TYPES.group_question:
      if (isNestedQuestionGroup) {
        name = `${questionId}[${fieldKey}][${rowIndex}][${nestedFieldKey}]`;
      } else {
        name = `${questionId}[${fieldKey}]`;
      }
      break;
    default:
  }
  return name;
};

const isEmergencyContactValid = (formFields) => {
  let emptyFieldCount = 0;
  const mandatoryEmergencyContactKeys = [emergencyContactRelationshipTypeKey, emergencyContactGivenName, emergencyContactFamilyName, emergencyContactPhoneNumber, emergencyContactEmail];
  const mandatoryEmergencyContactKeysLength = mandatoryEmergencyContactKeys.length;
  for (const key of mandatoryEmergencyContactKeys) {
    if (!formFields[key]) {
      emptyFieldCount += 1;
    }
  }
  return emptyFieldCount === 0 || emptyFieldCount === mandatoryEmergencyContactKeysLength;
}

class PaychexFormActionContent extends Component {
  constructor(props) {
    super(props);

    const {
      prefilledData = {}, dataFromEi = {}, action, companyStaff, config,
    } = props;

    let paychexCompanyId = null;

    if (config !== null) {
      paychexCompanyId = config.default_company_id;
    }

    this.handleSubmitForm = this.handleSubmitForm.bind(this);

    const lockedDataKeys = [];

    const formSubmission = (() => {
      const { assignees, action_type: actionType, status: actionStatus } = action;
      if (actionType === 'hr_fill_paychex_form' && actionStatus === 'not_started') {
        // get form_submission from current login company staff assignee
        const companyStaffAssignee = _.find(assignees, { company_staff: { id: companyStaff.id } });
        if (!companyStaffAssignee || !companyStaffAssignee.form_submission) return {};
        return companyStaffAssignee.form_submission;
      } if (actionType === 'hr_fill_paychex_form' && actionStatus !== 'not_started') {
        // get form_submission from master assignee
        const masterAssignee = _.find(assignees, { is_master: true });
        if (!masterAssignee || !masterAssignee.form_submission) return {};
        return masterAssignee.form_submission;
      }
      // For the 'employee_fill_adp_form' action, get form_submission from the first assignee
      return (assignees[0] && assignees[0].form_submission) || {};
    })();

    const { digest_key: formDigestKey, questions = [] } = formSubmission;

    let organizationsOptions = [], jobTitleOptions = [], supervisorOptions = [], emergencyRelationshipTypeOptions = [];
    const questionsWithDefaultAnswers =
      questions &&
      _.sortBy(questions, 'sequence').map((question) => {
        let questionWithAnswer = { ...question };
        switch (getComponentType(question)) {
          case TYPES.single_question:
            let answer = dataFromEi[question.key];
            if (!answer && answer !== '') answer = prefilledData[question.key] || '';
            if (answer) {
              lockedDataKeys.push(question.key);
              questionWithAnswer = set(
                `answer.answer.${question.key}`,
                answer,
                questionWithAnswer,
              );
            } else if (!questionWithAnswer.answer) {
              questionWithAnswer = set(
                `answer.answer.${question.key}`,
                '',
                questionWithAnswer,
              );
            }

            if (question.key === companyKey) {
              const value = questionWithAnswer.answer.answer[question.key];
              const options = this.getOptions(question);
              if (!value && options.length) {
                questionWithAnswer = set(
                  `answer.answer.${question.key}`,
                  options[0].codeValue,
                  questionWithAnswer,
                );
              }
            } else if (question.key === organizationKey) {
              const options = this.getOptions(question);
              organizationsOptions = options;
            } else if (question.key === jobTitleKey) {
              const options = this.getOptions(question);
              jobTitleOptions = options;
            } else if (question.key === supervisorKey) {
              const options = this.getOptions(question);
              supervisorOptions = options;
            }

            break;
          case TYPES.group_question:
            for (let i = 0; i < question.fields.length; i += 1) {
              const field = question.fields[i];

              if (
                question.answer
                && (question.answer.answer[field.key]
                  || question.answer.answer[field.key] === false)
              ) {
                if (field.key === countryCodeKey) {
                 this.loadSubdivision(question.answer.answer[field.key], countrySubdivisionCodeKey);
                }
                continue;
              } else if (field.key === emergencyContactRelationshipTypeKey) {
                const options = this.getOptions(field);
                emergencyRelationshipTypeOptions = options;
              }

              const answer = dataFromEi[field.key] || prefilledData[field.key] || '';
              if (answer) {
                lockedDataKeys.push(field.key);
              }
              if (field.fields) {
                field.fields.forEach((f) => {
                  questionWithAnswer = set(
                    `answer.answer.${field.key}[0].${f.key}`,
                    answer,
                    questionWithAnswer,
                  );
                });
              } else {
                questionWithAnswer = set(
                  `answer.answer.${field.key}`,
                  answer,
                  questionWithAnswer,
                );

                if (field.key === countryCodeKey) {
                  this.loadSubdivision(answer, countrySubdivisionCodeKey);
                } 
              }
            }
            break;
          default:
            break;
        }
        return questionWithAnswer;
      });
    this.state = {
      questions: questionsWithDefaultAnswers,
      errorMessageMap: {},

      // following states are for review process
      changedQuestions: {},
      // Do not change the value of `readOnlyQuestions`, this is only for query the original answer when reviewer uncheck
      // the question edit button
      readOnlyQuestions: { ...questionsWithDefaultAnswers },
      lockedDataKeys,
      countrySubdivisionOptions: [],
      jobTitleOptions: jobTitleOptions,
      organizationsOptions: organizationsOptions,
      supervisorOptions: supervisorOptions,
      formDigestKey,
      emergencyRelationshipTypeOptions: emergencyRelationshipTypeOptions,
    };
  }

  componentDidMount() {
    const { questions } = this.state;
    if (questions) {
      const emergencyContactQuestion = questions.find((e) => e.group == emergencyContactKey);  
      const companyQuestion = questions.find((e) => e.key == companyKey);
      if (emergencyContactQuestion) {
        this.loadCompanySpecificDataForEmployee(this.props.config.default_company_id);
      }
      if (companyQuestion) {
        const companyAnswer = companyQuestion.answer ? companyQuestion.answer.answer[companyKey] : null;
        if (companyAnswer) {
          this.loadCompanySpecificData(companyQuestion.answer.answer[companyKey]);
        }
      }
    }
  }

  handleSubmitForm(e) {
    e.preventDefault();
    const { action } = this.props;
    // validate form
    const { questions } = this.state;
    const errorMessageMap = {};
    for (let i = 0; i < questions.length; i += 1) {
      const question = questions[i];
      const componentType = getComponentType(question);
      switch (componentType) {
        case TYPES.single_question:
          const { rules } = question;
          let value = question.answer.answer[question.key];
          if (
            (!question.is_required || question.temporaryDisableRequired)
            && (value === '' || value.length === 0)
          ) continue;
          if (question.question_type === 'date' && value) {
            // date should be MM/DD/YYYY and can be converted to YYYY-MM-DD for validations
            if (!moment(value, 'MM/DD/YYYY', true).isValid()) {
              errorMessageMap[getQuestionInputName(question)] = 'Please use correct format MM/DD/YYYY';
              continue;
            } else {
              value = moment(value).format('YYYY-MM-DD');
            }
          }
          const error = getErrorMessage(value, rules);
          if (error) {
            errorMessageMap[getQuestionInputName(question)] = error;
          }
          break;
        case TYPES.group_question:
          for (let i = 0; i < question.fields.length; i += 1) {
            const fieldItem = question.fields[i];
            const nestedFields = getNestedFields(question);
            if (fieldItem && nestedFields.length !== 0) {
              const { fields } = nestedFields[0];
              for (let n = 0; n < fields.length; n += 1) {
                const nestedFieldItem = fields[n];
                for (
                  let rowIdx = 0;
                  rowIdx < question.answer.answer[nestedFields[0].key].length;
                  rowIdx += 1
                ) {
                  const { rules } = nestedFieldItem;
                  const value = question.answer.answer[nestedFields[0].key][rowIdx][
                    nestedFieldItem.key
                  ];
                  if (
                    (!nestedFieldItem.is_required
                      || nestedFieldItem.temporaryDisableRequired)
                    && (value === '' || value.length === 0)
                  ) continue;
                  const error = getErrorMessage(value, rules);
                  if (error) {
                    errorMessageMap[
                      getQuestionInputName(
                        question,
                        fieldItem.key,
                        rowIdx,
                        nestedFieldItem.key,
                      )
                    ] = error;
                  }
                }
              }
            }

            // Handle special checks for Address and Pay rate
            const { rules } = fieldItem;
            const value = question.answer.answer[fieldItem.key];
            if (fieldItem.key === streetLineOneKey && !value) {
              if (
                question.answer.answer[cityKey]
                || question.answer.answer[countrySubdivisionCodeKey]
                || question.answer.answer[postalCodeKey]
                || question.answer.answer[countryCodeKey]
              ) {
                errorMessageMap[getQuestionInputName(question, fieldItem.key)] = addressNote;
              }
            } else if (question.group === emergencyContactKey && !value && !isEmergencyContactValid(question.answer.answer)) {
                errorMessageMap[getQuestionInputName(question, fieldItem.key)] = emergencyContactNote;
            } else if (fieldItem.key === amountKey && !value) {
              if (question.answer.answer[rateTypeKey]) {
                errorMessageMap[getQuestionInputName(question, fieldItem.key)] = payrateNote;
              }
            } else if (fieldItem.key === cityKey && value && !(/^[A-Za-z\s'-]+$/.test(value))) {
              errorMessageMap[getQuestionInputName(question, fieldItem.key)] = 'Please enter a valid city name';
            }

            if (
              (!fieldItem.is_required || fieldItem.temporaryDisableRequired)
              && (value === '' || value.length === 0)
            ) continue;

            const error = getErrorMessage(value, rules);
            if (error) {
              errorMessageMap[getQuestionInputName(question, fieldItem.key)] = error;
            }
          }
          break;
        default:
      }
    }
    if (Object.keys(errorMessageMap).length > 0) {
      this.setState({ errorMessageMap });
      return;
    }

    if (this.props.previewMode) {
      alert("Congrats, but form won't be submitted in preview mode.");
      return;
    }

    const formPayload = this.state.questions.reduce((acc, question) => {
      const { onboarding_form_question_id: questionId } = question;

      if (question.question_type === 'date') {
        // here date should be MM/DD/YYYY and can be converted to YYYY-MM-DD
        let value = question.answer.answer[question.key];
        if (value) {
          value = moment(value, 'MM/DD/YYYY', true).isValid() ? moment(value).format('YYYY-MM-DD') : '';
          question = set(`answer.answer.${question.key}`, value, question);
        }
      }

      acc[questionId] = question.answer.answer;

      return acc;
    }, {});

    if (this.props.reviewActions) {
      const hasChanged = _.find(
        Object.keys(this.state.changedQuestions),
        (key) => this.state.changedQuestions[key],
      );
      return {
        hasChanged,
        answers: formPayload,
        id: action.assignees[0].form_submission.id,
      };
    }

    this.handleServerChecks(formPayload);
  }

  handleServerChecks(formPayload) {
    // Currently handling only supervisor for server side check,
    // if more checks are required in future then this method can be tweaked
    const { questions } = this.state;
    const { authCode, companyId } = this.props;
    let question;
    let value;
    let found = false;
    for (let i = 0; i < questions.length; i += 1) {
      if (found) {
        break;
      }
      question = questions[i];
      const componentType = getComponentType(question);
      switch (componentType) {
        case TYPES.single_question:
          if (question.key === supervisorWorkerIdKey) {
            value = question.answer.answer[question.key];
            found = true;
          }
          break;
        case TYPES.group_question:
          break;
        default:
      }
    }
    if (value) {
      const config = {
        headers: {
          ...(authCode && { Authorization: authCode }),
          ...(companyId && { 'ws-company-id': companyId }),
        },
        'Access-Control-Allow-Origin': '*',
      };
      const companyQuestion = this.state.questions.find((e) => e.key == companyKey);
      this.setState({ isSubmitting: true });
      Axios.post(
        `${APIHOST}/v1/onboarding/paychex/hr/companies/${companyQuestion.answer.answer[companyKey]}/verify_worker`,
        {
          worker_id: value,
        },
        config,
      )
        .then((response) => {
          if (response.data.message !== 'Valid worker') {
            this.addErrorToQuestion(
              question,
              "Supervisor's worker id is invalid.",
            );
          } else {
            this.submitFormDataToServer(formPayload);
          }
        })
        .catch((response) => {
          this.addErrorToQuestion(question, response.response.data.message);
        });
    } else {
      this.submitFormDataToServer(formPayload);
    }
  }

  addErrorToQuestion(question, message) {
    const errorMessageMap = {};
    errorMessageMap[getQuestionInputName(question, question.key)] = message;
    if (Object.keys(errorMessageMap).length > 0) {
      this.setState({ errorMessageMap });
    }
    this.setState({ isSubmitting: false });
  }

  submitFormDataToServer(formPayload) {
    const { companyId } = this.props;

    const config = {
      headers: {
        ...(authCode && { Authorization: authCode }),
        ...(companyId && { 'ws-company-id': companyId }),
      },
      'Access-Control-Allow-Origin': '*',
    };

    const digestKey = this.state.formDigestKey;
    this.setState({ isSubmitting: true });
    Axios.put(
      APIURL.replace(':digest_key', digestKey),
      {
        onboarding_form_submission: {
          answers: formPayload,
        },
      },
      config,
    )
      .then((data) => {
        this.setState({ isSubmitting: false });
        // Form success callback
        if (this.props.onSubmitPaychexFormSuccess) {
          this.props.onSubmitPaychexFormSuccess(data);
        }
      })
      .catch(() => {
        this.setState({ isSubmitting: false });
      });
  }

  handleTemporaryDisableRequired({ name, checked, question }) {
    let updatedQuestion = { ...question };
    updatedQuestion = set(`answer.answer.${question.key}`, '', updatedQuestion);
    updatedQuestion.temporaryDisableRequired = checked;

    const questions = this.state.questions.map((ques) => {
      if (
        ques.onboarding_form_question_id
        !== updatedQuestion.onboarding_form_question_id
      ) return q;
      return updatedQuestion;
    });

    const errorMessageMap = {
      ...this.state.errorMessageMap,
      [name]: undefined,
    };
    this.setState({ questions, errorMessageMap });
  }

  handleQuestionChange({
    name,
    value,
    question,
    fieldKey,
    rowIndex,
    fieldQuestion,
  }) {
    let updatedQuestion = { ...question };
    switch (getComponentType(question)) {
      case TYPES.single_question:
        updatedQuestion = set(
          `answer.answer.${question.key}`,
          value,
          updatedQuestion,
        );

        if (question.key === companyKey) {
          this.loadCompanySpecificData(value);
        }
        break;
      case TYPES.group_question:
        if (fieldQuestion && getNestedFields(question).length !== 0) {
          const combindValue = {
            ...updatedQuestion.answer.answer[`${fieldQuestion.key}`][
              `${rowIndex}`
            ],
          };
          combindValue[fieldKey] = value;
          updatedQuestion = set(
            `answer.answer.${fieldQuestion.key}[${rowIndex}]`,
            combindValue,
            updatedQuestion,
          );
        } else {
          updatedQuestion = set(
            `answer.answer.${fieldKey}`,
            value,
            updatedQuestion,
          );
        }

        if (fieldKey === countryCodeKey) {
          this.loadSubdivision(value, countrySubdivisionCodeKey);
        }
        break;
      default:
    }

    const questions = this.state.questions.map((q) => {
      if (
        q.onboarding_form_question_id
        !== updatedQuestion.onboarding_form_question_id
      ) return q;
      return updatedQuestion;
    });

    const errorMessageMap = {
      ...this.state.errorMessageMap,
      [name]: undefined,
    };
    this.setState({ questions, errorMessageMap });
  }

  loadSubdivision(countryValue, subdivisionCodeKey) {
    if (countryValue) {
      this.setState({ isSubmitting: true });
      const config = {
        headers: {
          ...(authCode && { Authorization: authCode }),
        },
        'Access-Control-Allow-Origin': '*',
      };
      Axios.get(`${APIHOST}/v1/countries/${countryValue}`, config)
        .then((response) => {
          const { subdivisions } = response.data;
          const subdivisionsOptions = Object.keys(subdivisions).map(function (
            key,
            index
          ) {
            return {
              codeValue: key,
              shortName:
                subdivisions[key].name || subdivisions[key].translations.en,
            };
          });
          if (subdivisionCodeKey === countrySubdivisionCodeKey) {
            this.setState({ countrySubdivisionOptions: subdivisionsOptions });
          }
          this.setState({ isSubmitting: false });
        })
        .catch((response) => {
          this.setState({ isSubmitting: false });
        });
    }
  }

  loadCompanySpecificData(companyValue) {
    if (companyValue) {
      this.setState({ isSubmitting: true });
      const { companyId } = this.props;
      const config = {
        headers: {
          ...(authCode && { Authorization: authCode }),
          ...(companyId && { 'ws-company-id': companyId }),
        },
        'Access-Control-Allow-Origin': '*',
        params: {
          dynamic_keys: [jobTitleKey, organizationKey, supervisorKey],
        },
      };
      Axios.get(`${APIHOST}/v1/onboarding/paychex/hr/companies/${companyValue}/dynamic_values`, config)
        .then((response) => {
          const dynamic_codelists = response.data;
          const jobTitleOptions = dynamic_codelists.find((e) => e.key == jobTitleKey).values;
          this.setState({ jobTitleOptions });
          const organizationsOptions = dynamic_codelists.find((e) => e.key == organizationKey).values;
          this.setState({ organizationsOptions });
          const supervisorOptions = dynamic_codelists.find((e) => e.key == supervisorKey).values;
          this.setState({ supervisorOptions });
          this.setState({ isSubmitting: false });
        })
        .catch((response) => {
          this.setState({ isSubmitting: false });
        });
    }
  }

  loadCompanySpecificDataForEmployee(companyValue) {
    if (companyValue) {
      this.setState({ isSubmitting: true });
      const { companyId } = this.props;
      const config = {
        params: {
          paychex_company_id: companyValue,
          dynamic_keys: [emergencyContactRelationshipTypeKey]
        }
      };
      
      const { action } = this.props;
  
      Axios.get(`${APIHOST}/v1/onboarding/paychex/employee/actions/${action.digest_key}/dynamic_values`, config)
      .then((response) => {
        const dynamic_codelists = response.data;
        const emergencyRelationshipTypeOptions = dynamic_codelists.find(e => e['key'] == emergencyContactRelationshipTypeKey).values;
        this.setState({ emergencyRelationshipTypeOptions: emergencyRelationshipTypeOptions });
        let emergencyQuestion = this.state.questions.find((q) => q.group === emergencyContactKey);
        const answerValue = this.props.dataFromEi ? this.props.dataFromEi["emergencyContact/relationship/relationshipType/relationshipTypeLabel"] : undefined;
        const relationshipTypeLabel = answerValue.charAt(0).toUpperCase() + answerValue.slice(1);

        const targetOption = emergencyRelationshipTypeOptions.find((o) => o.shortName === relationshipTypeLabel);
        if (relationshipTypeLabel !== ''){
          emergencyQuestion = set(
            `answer.answer.${emergencyContactRelationshipTypeKey}`,
            targetOption.codeValue,
            emergencyQuestion
          );
        }
        const buildedQuestions = this.state.questions.map((q) => {
          if (q.group !== emergencyContactKey) return q;
          return emergencyQuestion;
        });
        this.setState({ questions: buildedQuestions });
        this.setState({ isSubmitting: false });
      })
      .catch((response) => {
        this.setState({ isSubmitting: false });
      });
    }
  }

  handleQuestionEditChange(questionId, inputName, checked) {
    const changedQuestions = {
      ...this.state.changedQuestions,
      [questionId]: checked,
    };
    const errorMessageMap = {
      ...this.state.errorMessageMap,
      [inputName]: undefined,
    };
    let questions;
    if (!checked) {
      // bring back the original answer
      const questionInProps = _.find(this.state.readOnlyQuestions, {
        onboarding_form_question_id: questionId,
      });
      if (questionInProps) {
        questions = this.state.questions.map((question) => (question.onboarding_form_question_id === questionId
          ? questionInProps
          : question));
      }
    }
    this.setState({
      changedQuestions,
      errorMessageMap,
      ...(questions && { questions }),
    });
  }

  renderButtons() {
    // We have only one 'Submit' button in this component;
    if (this.props.reviewActions) return null;

    const { previewMode } = this.props;
    if (previewMode) {
      return <DisabledActionButton />;
    }

    let disabled = false;
    let disableNote = '';
    const {
      action: { assignees, action_type: actionType, status: actionStatus },
      companyStaff,
    } = this.props;

    if (actionStatus === 'completed') {
      return (
        <DisabledActionButton disableNote="This step has been completed." />
      );
    }

    if (
      actionStatus === 'not_started'
      && actionType === 'employee_fill_paychex_form'
      && companyStaff
      && companyStaff.id
    ) {
      disabled = true;
      disableNote = 'This step should be completed by the employee.';
    } else if (
      actionStatus === 'not_started'
      && actionType === 'hr_fill_paychex_form'
      && !isActionAssigneeMatchCompanyStaffV2(assignees, companyStaff)
    ) {
      const assigneeNames = (() => {
        if (!assignees || !assignees.length === 0 || !assignees[0]) return null;
        return assignees.map((item) => item.company_staff && item.company_staff.name);
      })();
      disabled = true;
      disableNote = `This step should be completed by ${assigneeNames.join(' or ')}.`;
    }

    return (
      <Flex column alignItems="flex-end">
        <Button
          disabled={disabled}
          onClick={this.handleSubmitForm}
          loading={this.state.isSubmitting ? 1 : 0}
        >
          Submit
        </Button>
        {disabled && <ActionNote text={disableNote} />}
      </Flex>
    );
  }

  getRequiredCategoryField(question) {
    const { questions: allQuestions } = this.state;
    let requiredQuestion = null;
    let requiredCategoryFieldTitle = null;

    if (question.category_type) {
      allQuestions.forEach((q) => {
        if (q.key === question.category_type) {
          requiredCategoryFieldTitle = q.title;
          requiredQuestion = q;
        } else if (q.fields) {
          q.fields.forEach((f) => {
            if (f.key === question.category_type) {
              requiredCategoryFieldTitle = f.title;
              requiredQuestion = q;
            }
          });
        }
      });
    }
    return { requiredQuestion, requiredCategoryFieldTitle };
  }

  noDataError(type, option, question, field) {
    if (type === 'dropdown' && option.length === 0) {
      if (
        field && field.key === countrySubdivisionCodeKey
        && question.answer.answer[countryCodeKey] === ''
      ) {
        return;
      }
      const title = field && question.fields && question.fields.length > 1
        ? field.title
        : question.title;
      return `There is no data setup for the question. Please check the Paychex company or turn '${title}' off temporarily.`;
    }
  }

  getRequiredCategoryFieldTitle(question) {
    const { requiredCategoryFieldTitle } = this.getRequiredCategoryField(question);
    return requiredCategoryFieldTitle;
  }

  getRequiredCategoryFieldAnswer(question, rowIdx) {
    const { requiredQuestion } = this.getRequiredCategoryField(question);

    let answer = requiredQuestion ? requiredQuestion.answer.answer : null;
    if (requiredQuestion && requiredQuestion.fields && rowIdx !== undefined) {
      if (requiredQuestion.max_items) {
        answer = requiredQuestion.answer.answer[rowIdx];
      }
    }
    return answer || answer === false ? answer[question.category_type] : '';
  }

  isRequiredCategoryValueExisted(question, rowIdx) {
    const answer = this.getRequiredCategoryFieldAnswer(question, rowIdx);
    return answer !== '';
  }

  requiredCategoryValue(question, rowIdx) {
    const answer = this.getRequiredCategoryFieldAnswer(question, rowIdx);
    return answer;
  }

  getOptions(field, rowIdx) {
    const options = field.options || (field.code_lists && field.code_lists[field.key]) || [];

    return options;
  }

  render() {
    this.isOnboardingV2 = isOnboardingV2 === 'true';
    const {
      reviewActions,
      viewResultAsManager,
      action: { status: actionStatus },
    } = this.props;
    const {
      questions,
      errorMessageMap,
      changedQuestions,
      lockedDataKeys,
      countrySubdivisionOptions,
      jobTitleOptions,
      organizationsOptions,
      supervisorOptions,
      readOnlyQuestions,
      emergencyRelationshipTypeOptions,
    } = this.state;

    if (!questions) {
      if (this.isOnboardingV2) {
        return (
          <Flex column>
            <PageTitle title={this.props.action.module_name} />
            <Text color="gray" mt={20}>
              No question to display.
            </Text>
          </Flex>
        );
      }
      return (
        <Flex mb={20} flexDirection="column">
          <Text fontSize="22px" bold>
            {this.props.title}
          </Text>
          <Text color="gray" mt={20}>
            No question to display.
          </Text>
        </Flex>
      );
    }

    // These are used as tricky way to show the sub section title. The section title only shows once.
    // State can be 0, 1, 2. O: no show, 1: show, 2: shown before
    let federalTaxSubTitleFlag = 0;
    let emergencyContactSubtitleFlag = 0;

    const items = questions.map((question) => {
      const { fields, onboarding_form_question_id: questionId } = question;

      const componentType = getComponentType(question);

      const originalQuestion = _.find(readOnlyQuestions, {
        onboarding_form_question_id: questionId,
      });

      switch (componentType) {
        case TYPES.single_question:
          let questionDisabled = question.disabled
            || viewResultAsManager
            || (reviewActions && changedQuestions[questionId])
            || (!!originalQuestion.answer.answer[question.key]
              && actionStatus === 'not_started'
              && lockedDataKeys.includes(question.key));
          let options = this.getOptions(question);
          let questionValue = question.answer.answer[question.key];
          let additionalNote = '';
          if (question.question_type === 'date') {
            // YYYY-MM-DD is loaded from backend, but show MM/DD/YYYY
            if (moment(questionValue, 'YYYY-MM-DD', true).isValid() || moment(questionValue, 'YYYY-MM-DDTHH:mm:ss.SSSSZ', true).isValid() || moment(questionValue).isValid()) {
              questionValue = moment(questionValue).format('MM/DD/YYYY');
              question.answer.answer[question.key] = questionValue;
            }
          } else if (question.key === companyKey) {
            questionDisabled = true;
          } else if (question.key === jobTitleKey) {
            if (question.answer.answer[companyKey] === '') {
              additionalNote = selectCompanyMessage;
            } else {
              options = jobTitleOptions;
            }
          } else if (question.key === organizationKey) {
            if (question.answer.answer[companyKey] === '') {
              additionalNote = selectCompanyMessage;
            } else {
              options = organizationsOptions;
            }
          } else if (question.key === supervisorKey) {
            if (question.answer.answer[companyKey] === '') {
              additionalNote = selectCompanyMessage;
            } else {
              options = supervisorOptions;
            }
          }

          if (federalTaxSubTitleFlag === 1) {
            federalTaxSubTitleFlag = 2;
          }
          if (question.key.includes(federalTaxKey) && federalTaxSubTitleFlag === 0) {
            federalTaxSubTitleFlag = 1;
          }

          const value = questionValue || questionValue === false ? questionValue : '';
          return (
            <div>
            {federalTaxSubTitleFlag === 1 ? (renderSubTitle(subSectionTitleFederalTax)) : <></>}
            <Question
              key={questionId}
              title={question.title}
              name={getQuestionInputName(question)}
              errorMessage={errorMessageMap[getQuestionInputName(question)]}
              additionalNote={
                errorMessageMap[getQuestionInputName(question, question.key)]
                  ? ''
                  : additionalNote
              }
              value={value}
              type={question.question_type}
              options={options}
              optionLabelDescriptionFirst
              isRequired={
                !!question.is_required && !question.temporaryDisableRequired
              }
              dateFormat={
                question.date_format && question.date_format.toUpperCase()
              }
              onChange={({ name, value }) => {
                this.handleQuestionChange({
                  name,
                  value,
                  question,
                });
              }}
              temporaryDisableRequired={question.temporaryDisableRequired}
              disabled={questionDisabled}
              reviewActions={reviewActions}
              isEditChecked={changedQuestions[questionId]}
              onCheckEdit={(checked) => {
                const inputName = getQuestionInputName(question);
                this.handleQuestionEditChange(questionId, inputName, checked);
              }}
              categoryType={question.category_type}
              isRequiredCategoryValueExisted={this.isRequiredCategoryValueExisted(
                question,
              )}
              requiredCategoryValue={this.requiredCategoryValue(question)}
              requiredCategoryTitle={this.getRequiredCategoryFieldTitle(
                question,
              )}
              noDataError={this.noDataError(
                question.question_type,
                options,
                question,
              )}
            />
            </div>
          );
          break;
        case TYPES.group_question:
          const subItems = fields.map((field, idx) => {
            const questionDisabled = field.disabled
              || viewResultAsManager
              || (reviewActions && changedQuestions[questionId])
              || (!!originalQuestion.answer.answer[field.key]
                && actionStatus === 'not_started'
                && !lockedDataKeys.includes(field.key));

            let options = this.getOptions(field);
            const questionValue = question.answer.answer[field.key];
            let additionalNote = '';
            if (field.key === streetLineOneKey) {
              additionalNote = addressNote;
            } else if (field.key === amountKey) {
              additionalNote = payrateNote;
            } else if (field.key === countrySubdivisionCodeKey) {
              if (question.answer.answer[countryCodeKey] === '') {
                additionalNote = selectCountryMessage;
              } else {
                options = countrySubdivisionOptions;
              }
            } else if (field.key === emergencyContactRelationshipTypeKey) {
                options = emergencyRelationshipTypeOptions;                               
            }

            if (emergencyContactSubtitleFlag === 1) {
              emergencyContactSubtitleFlag = 2;
            }
            if (field.key.includes(emergencyContactGroupKey) && emergencyContactSubtitleFlag === 0) {
              emergencyContactSubtitleFlag = 1;
            }
            return (
              <div>
                {emergencyContactSubtitleFlag === 1 ? (renderSubTitle(subSectionTitleEmergencyContact)):<></>}
                <Question
                  key={`${questionId}-${field.key}`}
                  title={field.title}
                  name={getQuestionInputName(question, field.key)}
                  type={field.question_type}
                  value={
                    questionValue || questionValue === false ? questionValue : ''
                  }
                  errorMessage={
                    errorMessageMap[getQuestionInputName(question, field.key)]
                  }
                  additionalNote={
                    errorMessageMap[getQuestionInputName(question, field.key)]
                      ? ''
                      : additionalNote
                  }
                  options={options}
                  optionLabelDescriptionFirst
                  isRequired={
                    !!field.is_required && !field.temporaryDisableRequired
                  }
                  dateFormat={
                    field.date_format && field.date_format.toUpperCase()
                  }
                  onChange={({ name, value }) => {
                    this.handleQuestionChange({
                      name,
                      value,
                      question,
                      fieldKey: field.key,
                   });
                  }}
                  disabled={questionDisabled}
                  reviewActions={reviewActions}
                  isEditChecked={changedQuestions[questionId]}
                  onCheckEdit={(checked) => {
                    const inputName = getQuestionInputName(question, field.key);
                    this.handleQuestionEditChange(questionId, inputName, checked);
                  }}
                  categoryType={field.category_type}
                  isRequiredCategoryValueExisted={this.isRequiredCategoryValueExisted(
                    field,
                  )}
                  requiredCategoryValue={this.requiredCategoryValue(field)}
                  requiredCategoryTitle={this.getRequiredCategoryFieldTitle(
                    field,
                  )}
                  noDataError={this.noDataError(
                    field.question_type,
                    options,
                    question,
                    field,
                  )}
                />
              </div>
            );
          });
          return (
            <QuestionGroup key={questionId} extendable={false}>
              {subItems}
            </QuestionGroup>
          );
          break;
        default:
      }
    });

    return (
      <form
        style={{
          width: this.props.reviewActions ? '310px' : '280px',
          margin: 'auto',
        }}
      >
        {
          this.isOnboardingV2 && <PageTitle title={this.props.action.module_name} />
        }
        {
          !this.isOnboardingV2
            && <Flex mb={20}><Text fontSize="22px" bold>{this.props.title}</Text></Flex>
        }
        {renderSubTitle(subSectionTitlePersonalInformation)}
        <Flex column ml={this.props.reviewActions && 24}>
          {items}
          {
            this.state.isSubmitting && (<Loading className="spinner spinner-blue" />)
          }
        </Flex>
        {this.renderButtons()}
      </form>
    );
  }
}

export default PaychexFormActionContent;
